// src/api/adminResults.ts
import { V1, authFetch } from "api/client";

export type AdminOrderResultStatus = "draft" | "published" | "archived";
export type AdminOrderResultEntryMethod = "upload" | "manual";
export type AdminOrderResultFileRole = "original" | "branded" | null;

export interface AdminOrderResultRowOut {
  id: number;
  order_id: number;

  title?: string | null;
  summary?: string | null;

  entry_method_code: AdminOrderResultEntryMethod;
  file_role_code: AdminOrderResultFileRole;
  source_result_id?: number | null;

  status: AdminOrderResultStatus;

  file_name?: string | null;
  file_url?: string | null;
  mime_type?: string | null;
  size_bytes?: number | null;

  uploaded_at?: string | null;
  published_at?: string | null;

  lock_version: number;
  created_date: string;
  last_updated_date?: string | null;
}

export interface AdminOrderResultDetailOut extends AdminOrderResultRowOut {
  content_json?: any | null;
  content_text?: string | null;
}

/* ===================== Structured content schema ===================== */

export interface StructuredPatient {
  first_name?: string | null;
  last_name?: string | null;
  date_of_birth?: string | null; // YYYY-MM-DD
  sex?: string | null;
  collected_at?: string | null; // ISO8601 datetime
  lab_name?: string | null;
}

export interface StructuredMarker {
  code?: string | null;
  name: string;
  value: string;
  unit?: string | null;
  ref_low?: number | null;
  ref_high?: number | null;
  flag?: string | null;
  notes?: string | null;

  /**
   * Optional backend-facing inference lookup metadata.
   * Useful when UI grouping differs from backend blueprint panel context.
   */
  biomarker_id?: string | null;
  panel_id?: string | null;
  panel_title?: string | null;
}

export interface StructuredResult {
  patient?: StructuredPatient;
  markers: StructuredMarker[];
  notes?: string | null;
  schema_version?: string | null;
}

export interface AdminManualResultIn {
  title?: string | null;
  summary?: string | null;
  generate_pdf?: boolean;
  content_json: StructuredResult | any;
}

export interface AdminManualResultOut {
  structured: AdminOrderResultDetailOut;
  branded_pdf?: AdminOrderResultRowOut | null;
}

export interface AdminUpdateOrderResultIn {
  expected_lock_version: number;
  title?: string | null;
  summary?: string | null;
  content_json?: StructuredResult | any | null;
}

export interface AdminDeleteOrderResultIn {
  expected_lock_version: number;
  cascade?: boolean;
  delete_files?: boolean;
}

/** NEW: /copy body */
export interface AdminCopyOrderResultIn {
  expected_lock_version: number;
  title?: string | null;
  summary?: string | null;
  file_name?: string | null;
}

/* ===================== Helpers ===================== */

function normalizeRowList(res: any): AdminOrderResultRowOut[] {
  if (Array.isArray(res)) return res as AdminOrderResultRowOut[];
  if (Array.isArray(res?.items)) return res.items as AdminOrderResultRowOut[];
  if (Array.isArray(res?.results)) return res.results as AdminOrderResultRowOut[];
  return [];
}

/* ===================== API functions ===================== */

export async function adminListOrderResults(orderId: number): Promise<AdminOrderResultRowOut[]> {
  const res = await authFetch<any>(`${V1}/admin/orders/${orderId}/results`, {
    method: "GET",
  });
  return normalizeRowList(res);
}

export async function adminGetOrderResultDetail(
  orderId: number,
  resultId: number
): Promise<AdminOrderResultDetailOut> {
  return authFetch<AdminOrderResultDetailOut>(
    `${V1}/admin/orders/${orderId}/results/${resultId}`,
    { method: "GET" }
  );
}

export async function adminUploadOrderResult(
  orderId: number,
  args: { file: File; summary?: string; expected_lock_version?: number }
): Promise<AdminOrderResultRowOut[]> {
  const fd = new FormData();
  fd.append("file", args.file);
  if (args.summary && args.summary.trim()) fd.append("summary", args.summary.trim());
  if (typeof args.expected_lock_version === "number") {
    fd.append("expected_lock_version", String(args.expected_lock_version));
  }

  const res = await authFetch<any>(`${V1}/admin/orders/${orderId}/results`, {
    method: "POST",
    body: fd,
  });

  return normalizeRowList(res);
}

export async function adminCreateManualResult(
  orderId: number,
  payload: AdminManualResultIn
): Promise<AdminManualResultOut> {
  return authFetch<AdminManualResultOut>(
    `${V1}/admin/orders/${orderId}/results/manual`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
}

/** NEW: single-step copy (should create ONE branded row with a PDF) */
export async function adminCopyOrderResult(
  orderId: number,
  resultId: number,
  payload: AdminCopyOrderResultIn
): Promise<AdminOrderResultDetailOut> {
  return authFetch<AdminOrderResultDetailOut>(
    `${V1}/admin/orders/${orderId}/results/${resultId}/copy`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
}

export async function adminDeleteOrderResult(
  orderId: number,
  resultId: number,
  payload: AdminDeleteOrderResultIn
): Promise<void> {
  await authFetch<void>(`${V1}/admin/orders/${orderId}/results/${resultId}`, {
    method: "DELETE",
    body: JSON.stringify(payload),
  });
}

export async function adminUpdateOrderResult(
  orderId: number,
  resultId: number,
  payload: AdminUpdateOrderResultIn
): Promise<AdminOrderResultDetailOut> {
  return authFetch<AdminOrderResultDetailOut>(
    `${V1}/admin/orders/${orderId}/results/${resultId}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    }
  );
}